import { pdf } from '@react-pdf/renderer';
import { LaudoDocument } from '../LaudoDocument';
import { LaudoData } from '../../types';
import { LaudoWizardData } from '../../types/laudo-wizard.types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { transformLaudoWizardToLaudoData } from '../../utils/transformLaudoData';
import { fetchChecklistQuestionarioData } from '../../utils/fetchChecklistQuestionario';
import { refreshLaudoPhotos, getBariLogoBase64, blobToBase64 } from './imageUtils';
import { docmanService } from '@/services/docman.service';
import { streetViewService } from '@/services/streetview.service';

/**
 * Hidrata `wizardData.vistoria.fotoFachada` baixando do Docman quando há `fotoFachadaDocumentId`.
 * Necessário porque o `reportData` salvo nunca contém o base64 da foto — só o documentId.
 */
async function hydrateFotoFachada(wizardData: LaudoWizardData): Promise<LaudoWizardData> {
  const fotoFachadaDocumentId = wizardData.vistoria?.fotoFachadaDocumentId;
  const jaTemBase64 = wizardData.vistoria?.fotoFachada;

  if (!fotoFachadaDocumentId || jaTemBase64) {
    return wizardData;
  }

  try {
    const blob = await docmanService.downloadFile(fotoFachadaDocumentId);
    const base64 = await blobToBase64(blob);
    return {
      ...wizardData,
      vistoria: {
        ...wizardData.vistoria,
        fotoFachada: base64,
      },
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Falha ao carregar foto de fachada (docId=${fotoFachadaDocumentId}): ${msg}`);
  }
}

/**
 * Para laudos automáticos: busca a imagem do Street View do endereço do imóvel.
 * Replica a mesma lógica usada no AutomaticLaudoGenerator.
 */
async function fetchStreetViewForAutomatic(inspection: InspectionRequest): Promise<string | undefined> {
  const firstItem = inspection.items?.[0];
  if (!firstItem?.street || !firstItem?.city) {
    return undefined;
  }

  try {
    const number = firstItem.number || 'S/N';
    const district = firstItem.district || '';
    const state = firstItem.state || '';
    const cep = firstItem.cep || '';
    const endereco = `${firstItem.street}, ${number}, ${district}, ${firstItem.city}/${state}, ${cep}`;

    const images = await streetViewService.fetchMultipleStreetViewAngles(endereco);
    return images.length > 0 ? images[0].base64 : undefined;
  } catch (err) {
    console.warn('[generatePdfOnTheFly] Falha ao buscar Street View:', err);
    return undefined;
  }
}

interface GeneratePdfOptions {
  wizardData: LaudoWizardData;
  inspection: InspectionRequest;
  isAutomatic: boolean;
}

/**
 * Gera o PDF on-the-fly a partir do wizardData salvo. Hidrata a foto de fachada
 * (e o Street View, no caso automático) antes de renderizar.
 */
async function buildPdfDocument(opts: GeneratePdfOptions) {
  const hydrated = await hydrateFotoFachada(opts.wizardData);

  // No layout normal (não-automático), o relatório fotográfico depende das fotos do checklist.
  // Buscar o questionário aqui garante que ele apareça no preview/upload on-the-fly.
  const questionarioData = opts.isAutomatic
    ? []
    : await fetchChecklistQuestionarioData(opts.inspection);

  const laudoDataRaw: LaudoData = transformLaudoWizardToLaudoData(hydrated, opts.inspection, questionarioData);
  const laudoData = await refreshLaudoPhotos(laudoDataRaw);

  let logoBase64: string | undefined;
  try {
    logoBase64 = await getBariLogoBase64();
  } catch {
    logoBase64 = undefined;
  }

  let streetViewBase64: string | undefined;
  if (opts.isAutomatic) {
    streetViewBase64 = hydrated.vistoria?.fotoFachada
      || await fetchStreetViewForAutomatic(opts.inspection);
  }

  return (
    <LaudoDocument
      data={laudoData}
      logoBase64={logoBase64}
      isAutomatic={opts.isAutomatic}
      {...(streetViewBase64 ? { streetViewImageBase64: streetViewBase64 } : {})}
    />
  );
}

export async function generatePdfBlobUrl(opts: GeneratePdfOptions): Promise<string> {
  const doc = await buildPdfDocument(opts);
  const pdfBlob = await pdf(doc).toBlob();
  return URL.createObjectURL(pdfBlob);
}

export async function generatePdfFile(opts: GeneratePdfOptions, fileName: string): Promise<File> {
  const doc = await buildPdfDocument(opts);
  const pdfBlob = await pdf(doc).toBlob();
  return new File([pdfBlob], fileName, { type: 'application/pdf' });
}
